import { useState, useRef, useEffect, useMemo, type KeyboardEvent } from 'react';
import { search, localName, flagSrc, type Country } from '../lib/countries';
import { useI18n } from '../i18n';

// 自定义国家补全:可选国旗 + 高亮匹配 + 键盘导航 + 点击外部关闭。
// hideFlags:Flag 模式下隐藏下拉国旗,避免比对题面国旗作弊。
export default function GuessInput({
  onGuess,
  hideFlags = false,
}: {
  onGuess: (c: Country) => void;
  hideFlags?: boolean;
}) {
  const { t, lang } = useI18n();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const matches = useMemo(() => (q.trim() ? search(q, lang, 7) : []), [q, lang]);

  useEffect(() => setHi(0), [q]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // 自动聚焦但不滚动(避免 focus 引发的强制重排 / forced reflow)
  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, []);

  function choose(c: Country) {
    onGuess(c);
    setQ('');
    setOpen(false);
    inputRef.current?.focus({ preventScroll: true });
  }

  function commit() {
    const c = matches[hi] ?? matches[0];
    if (c) choose(c);
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else setHi((h) => Math.min(h + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  function renderName(c: Country) {
    const name = localName(c, lang);
    const term = q.trim().toLowerCase();
    const i = term ? name.toLowerCase().indexOf(term) : -1;
    if (i < 0) return name;
    return (
      <>
        {name.slice(0, i)}
        <mark>{name.slice(i, i + term.length)}</mark>
        {name.slice(i + term.length)}
      </>
    );
  }

  return (
    <div className="ac" ref={boxRef}>
      <div className="ac-field">
        <svg className="ac-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
          <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => q && setOpen(true)}
          onKeyDown={onKey}
          placeholder={t.placeholder}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          role="combobox"
          aria-expanded={open}
          aria-controls="ac-list"
          aria-autocomplete="list"
        />
        <button type="button" className="ac-go" onClick={commit}>
          {t.guess}
        </button>
      </div>

      {open && matches.length > 0 && (
        <ul className="ac-list" id="ac-list" role="listbox">
          {matches.map((c, i) => (
            <li
              key={c.cca3}
              role="option"
              aria-selected={i === hi}
              className={i === hi ? 'hi' : ''}
              onMouseEnter={() => setHi(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(c);
              }}
            >
              {!hideFlags && <img src={flagSrc(c)} alt="" loading="lazy" />}
              <span className="ac-name">{renderName(c)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
